import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime, skip } from 'rxjs/operators';
import { MatTooltipModule } from '@angular/material/tooltip';

// services
import { ContactsService } from '../../../services/contacts/contacts.service';
import { MessageService } from '../../../services/message/message.service';
import { GroupAudienceService } from '../../../services/segments/group-audience.service';
import { SegmentService } from '../../../services/segments/segment-service.service';

// types
import { PaginationComponent } from '../../inputs/pagination/pagination.component';
import {
  IEntitySegmentQuery,
  describeFilters,
  hasLegacyFilters,
} from '../../organization/segments/segment-utilities';
import { PromptCardComponent } from '../../toasts-and-cards/prompt-card/prompt-card.component';
import { IPagination } from '../../../types/contactModule';
import { EmptyStateComponent } from '../../ui/empty-state/empty-state.component';
import { DataTableComponent } from '../../ui/data-table/data-table.component';
import { HasPermissionDirective } from '../../../directives/has-permission.directive';
import { formatCount, formatWhen } from '../../../../utils/sms';
import { CountState, GroupSelection, countNoun } from '../group-table/group-rows';

interface SegmentRow {
  segment: IEntitySegmentQuery;
  key: string;
  name: string;
  rule: string;
  legacy: boolean;
  createdBy: string;
  updated: string;
  updatedTitle: string | null;
}

function toRows(segments: IEntitySegmentQuery[]): SegmentRow[] {
  return (segments ?? [])
    .filter((segment) => !!segment && typeof segment === 'object')
    .map((segment, index) => {
      const updated = segment.modifiedAt || segment.createdAt;
      return {
        segment,
        key: segment.uuid ? String(segment.uuid) : segment.id != null ? `id-${segment.id}` : `row-${index}`,
        name: (segment.name ?? '').toString().trim() || 'Untitled segment',
        rule: describeFilters(segment.entitySegmentQueryFilters),
        legacy: hasLegacyFilters(segment.entitySegmentQueryFilters),
        createdBy: (segment.createdBy?.login ?? '').toString().trim() || '—',
        updated: formatWhen(updated),
        updatedTitle: updated ? new Date(updated).toLocaleString('en-GB') : null,
      };
    });
}

@Component({
  selector: 'segmented-table',
  standalone: true,
  imports: [
    PaginationComponent,
    PromptCardComponent,
    EmptyStateComponent,
    DataTableComponent,
    HasPermissionDirective,
    MatTooltipModule,
  ],
  templateUrl: './segmented-table.component.html',
  styleUrls: ['../group-table/group-table.css', './segmented-table.component.css'],
})
export class SegmentedTableComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  private messageService = inject(MessageService);
  private audience = inject(GroupAudienceService);
  segmentService = inject(SegmentService);
  contactsService = inject(ContactsService);
  router = inject(Router);

  /** Search and sort from the list page. A new object on each change. */
  @Input() params: Record<string, any> = {};
  @Input() searchTerm = '';
  @Output() getSelections: EventEmitter<IEntitySegmentQuery[]> = new EventEmitter();
  @Output() totalChange = new EventEmitter<number>();
  @Output() clearFilters = new EventEmitter<void>();

  readonly formatCount = formatCount;

  segments: IEntitySegmentQuery[] = [];
  rows: SegmentRow[] = [];
  counts: Record<string, CountState> = {};

  loading = true;
  loadError = '';
  pageIndex = 0;
  pageSize = 10;
  private loadSub?: Subscription;
  private started = false;

  readonly selection = new GroupSelection<IEntitySegmentQuery>();

  // selected segment for the purposes of deletion
  selectedSegment: IEntitySegmentQuery | null = null;
  showDeleteModal: boolean = false;
  deleting = false;

  segmentPagination: IPagination = {
    totalElements: 0,
    pageSize: 10,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  };

  // ── Loading ──
  load() {
    this.started = true;
    this.loadSub?.unsubscribe();
    this.loading = true;
    this.loadError = '';
    this.loadSub = this.audience
      .listSegments({ ...this.params, page: this.pageIndex, size: this.pageSize })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (page) => {
          const pagination = page.itemPagination;
          const lastPage = Math.max(0, (pagination?.totalPages ?? 1) - 1);
          if (!page.items.length && this.pageIndex > lastPage && (pagination?.totalElements ?? 0) > 0) {
            this.pageIndex = lastPage;
            this.load();
            return;
          }
          this.segments = page.items;
          this.rows = toRows(page.items);
          this.segmentPagination = { ...pagination, pageNumber: this.pageIndex };
          this.selection.refresh(page.items);
          this.loading = false;
          this.totalChange.emit(pagination?.totalElements ?? page.items.length);
          this.loadCounts();
        },
        error: () => {
          this.loading = false;
          this.loadError = 'Segments did not load. Check your connection and try again.';
        },
      });
  }

  private loadCounts() {
    for (const row of this.rows) {
      const uuid = row.segment.uuid;
      if (!uuid || typeof this.counts[uuid] === 'number') continue;
      this.counts[uuid] = undefined;
      this.audience
        .memberCount({ kind: 'segment', uuid })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (count) => (this.counts = { ...this.counts, [uuid]: count }),
          error: () => (this.counts = { ...this.counts, [uuid]: null }),
        });
    }
  }

  countOf(segment: IEntitySegmentQuery | null): CountState {
    return segment?.uuid ? this.counts[segment.uuid] : null;
  }

  countState(segment: IEntitySegmentQuery): 'loading' | 'none' | 'known' {
    const count = this.countOf(segment);
    return count === undefined ? 'loading' : count === null ? 'none' : 'known';
  }

  // ── Paging ──
  goToPreviousPage() {
    this.updateSegmentPage(this.pageIndex - 1);
  }
  goToNextPage() {
    this.updateSegmentPage(this.pageIndex + 1);
  }
  updateSegmentPage(newPage: number) {
    this.pageIndex = Math.max(0, newPage);
    this.load();
  }
  /** The pager follows a size change with changePage(0), which loads. */
  onPageSizeChange(size: number) {
    this.pageSize = size;
  }

  // ── Selection ──
  selectSegment(segment: IEntitySegmentQuery) {
    this.selection.toggle(segment);
    this.emitSelections();
  }

  deSelectAllSegments() {
    this.selection.clear();
    this.emitSelections();
  }

  getSegmentIsSelected(segment: IEntitySegmentQuery) {
    return this.selection.has(segment);
  }

  toggleSelectAllSegments() {
    this.selection.togglePage(this.segments);
    this.emitSelections();
  }

  get getIsAllSelected() {
    return this.selection.allOn(this.segments);
  }

  get someOnPageSelected() {
    return this.selection.someOn(this.segments);
  }

  emitSelections() {
    this.getSelections.emit(this.selection.items);
  }

  // ── Row actions ──
  goToSegmentDetails(id: string | number) {
    if (id) this.router.navigate([`organization/contacts/segmented-list/${id}`]);
  }

  openFromKeyboard(event: Event, segment: IEntitySegmentQuery) {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.goToSegmentDetails(segment.uuid || '');
  }

  /** Opens the composer with this segment already picked. */
  messageSegment(segment: IEntitySegmentQuery) {
    this.messageService.updateSelectedSegments([segment]);
    this.router.navigate(['/organization/messages/create']);
  }

  handleGoToEditSegment(segment: IEntitySegmentQuery) {
    this.segmentService.startEditing(segment);
    this.router.navigate([`organization/contacts/segmented-list/edit/${segment.uuid}`]);
  }

  openDeleteModal(segment: IEntitySegmentQuery) {
    this.selectedSegment = segment;
    this.showDeleteModal = true;
  }
  closeDeleteModal() {
    if (this.deleting) return;
    this.showDeleteModal = false;
  }

  get deleteImpact(): string {
    const count = this.countOf(this.selectedSegment);
    if (typeof count !== 'number') return 'Its contacts stay in your contact list; only the saved conditions are removed.';
    if (count === 0) return 'No contacts match it today.';
    return `${countNoun(count, 'contact matches', 'contacts match')} it today. They stay in your contact list; only the saved conditions are removed.`;
  }

  handleDeleteSegment() {
    const segment = this.selectedSegment;
    if (!segment?.uuid || this.deleting) return;
    this.deleting = true;
    this.contactsService.deleteSegment(segment.uuid).subscribe({
      next: () => {
        this.deleting = false;
        this.selection.remove([segment]);
        this.emitSelections();
        this.showDeleteModal = false;
        this.audience.invalidate({ kind: 'segment', uuid: segment.uuid! });
        this.contactsService.showSuccess(`${segment.name || 'The segment'} was deleted.`);
      },
      error: () => {
        this.deleting = false;
        this.showDeleteModal = false;
        this.contactsService.showError(`${segment.name || 'The segment'} was not deleted. Try again.`);
      },
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['params']) {
      this.pageIndex = 0;
      this.load();
    }
  }

  ngOnInit() {
    if (!this.started) this.load();

    // ContactsService reloads its shared list after a create, edit or delete;
    // reload this page with its own search and sort.
    this.contactsService.getSegments
      .pipe(skip(1), debounceTime(150), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());
  }
}
